import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { MccApi } from "lib/api/mccApiWrapper";
import { UsePermissionRefresh } from "./useSectorsAndPermissions";
import { UserSlice } from "store";
import { UserRoleNote } from "models/dto/user";
import * as opt from "lib/opt";
import { UseSnackBar } from "./misc";
import * as result from "lib/result";

export interface PlantNote {
	id: string;
	name: string;
	disabled: boolean;
	sequence?: number;
}

export interface PlantWithNotes {
	id: string;
	name: string;
	userRoleId: string;
	notes: PlantNote[];
}

export interface SectorWithPlants {
	id: string;
	name: string;
	plants: PlantWithNotes[];
}

export function UseUserRoleNotes() {
	const [sectorsWithPlants, setSectorsWithPlants] = useState<
		SectorWithPlants[]
	>([]);
	const [loading, setLoading] = useState(false);
	// Track mappings: plantId -> userRoleId
	const [plantToUserRoleId, setPlantToUserRoleId] = useState<
		Record<string, string>
	>({});


	const user = useSelector(UserSlice.selectors.selectUser);
	const userRoles = useSelector(UserSlice.selectors.selectUserRoles);
	const { handlePermissionError } = UsePermissionRefresh();
	const snack = UseSnackBar();

	const fetchUserRoleNotes = useCallback(async () => {
		if (!user?.id) {
			snack.open(result.Err("User information not available"));
			setLoading(false);
			return;
		}

		try {
			setLoading(true);

			const api = MccApi.GetInstance((window as any).store);
			const userRoleNotes = await api.GetUserRoleNotesByBusinessUserId(
				user.id
			);

			// Build structure: Sector -> Plant -> Notes
			const sectorMap = new Map<
				string,
				{
					id: string;
					name: string;
					plants: Map<
						string,
						{
							id: string;
							name: string;
							userRoleId: string;
							notes: Map<string, PlantNote>;
						}
					>;
				}
			>();
			const plantToRoleMap: Record<string, string> = {};

			userRoleNotes.forEach((userRoleNote: UserRoleNote) => {
				const userRole = userRoleNote.userRole;
				if (!userRole) return;

				// Map sector from userRoleNote -> userRole -> sector
				const sector = userRole.sector;
				const sectorId = sector?.id;
				const sectorName = sector?.name;

				// Map plant from userRoleNote -> userRole -> plant
				const plant = userRole.plant;
				const plantId = plant?.id;
				const plantName = plant?.name;

				// Map note from userRoleNote -> note
				const note = userRoleNote.note;
				const noteId = note?.id;
				const noteName = note?.name;
				const noteSequence = note?.sequence;
				const disabled = userRoleNote.disabled ?? true;

				const userRoleId = userRole.id || "";

				// Skip if required data is missing
				if (
					!sectorId ||
					!sectorName ||
					!plantId ||
					!plantName ||
					!noteId ||
					!noteName
				) {
					return;
				}

				// Get or create sector
				if (!sectorMap.Contains(sectorId)) {
					sectorMap.Set(sectorId, {
						id: sectorId,
						name: sectorName,
						plants: new Map(),
					});
				}

				const sectorDataOpt = sectorMap.Get(sectorId);
				if (sectorDataOpt.IsNone()) return;
				const sectorData = sectorDataOpt.Unwrap();

				// Get or create plant
				if (!sectorData.plants.Contains(plantId)) {
					sectorData.plants.Set(plantId, {
						id: plantId,
						name: plantName,
						userRoleId: userRoleId,
						notes: new Map(),
					});
				}

				const plantDataOpt = sectorData.plants.Get(plantId);
				if (plantDataOpt.IsNone()) return;
				const plantData = plantDataOpt.Unwrap();

				plantToRoleMap[plantId] = userRoleId;

				plantData.notes.Set(noteId, {
					id: noteId,
					name: noteName,
					disabled: disabled,
					sequence: noteSequence,
				});

				});

			const sectorsArray: SectorWithPlants[] = sectorMap
				.Values()
				.Map((sector) => ({
					id: sector.id,
					name: sector.name,
					plants: sector.plants
						.Values()
						.Map((plant) => ({
							id: plant.id,
							name: plant.name,
							userRoleId: plant.userRoleId,
							notes: plant.notes.Values(),
						}))
						.Sort((a, b) =>
							(a.name || "").localeCompare(b.name || "", undefined, {
								numeric: true,
								sensitivity: "base",
							})
						),
				}))
				.Sort((a, b) =>
					(a.name || "").localeCompare(b.name || "", undefined, {
						numeric: true,
						sensitivity: "base",
					})
				);

			setSectorsWithPlants(sectorsArray);
			setPlantToUserRoleId(plantToRoleMap);
		} catch (e: any) {
			snack.open(
				result.Err(
					e?.message || "Failed to load notification settings."
				)
			);
			setSectorsWithPlants([]);
		} finally {
			setLoading(false);
		}
	}, [user, userRoles, handlePermissionError, snack]);

	useEffect(() => {
		fetchUserRoleNotes();
	}, [fetchUserRoleNotes]);

	const updateNotificationSetting = useCallback(
		async (userRoleId: string, noteId: string, value: boolean) => {
			if (!userRoleId || !noteId) {
				snack.open(
					result.Err(
						"Unable to update notification setting: missing user role or note information"
					)
				);
				return;
			}

			// Optimistically update UI: only the plant for this userRoleId (one per sector+plant)
			setSectorsWithPlants((prev) =>
				prev.Map((sector) => ({
					...sector,
					plants: sector.plants.Map((plant) => {
						if (plant.userRoleId !== userRoleId) return plant;
						return {
							...plant,
							notes: plant.notes.Map((note) =>
								note.id === noteId
									? { ...note, disabled: !value }
									: note
							),
						};
					}),
				}))
			);

			try {
				const api = MccApi.GetInstance((window as any).store);
				await api.UpdateUserRoleNote(userRoleId, noteId);
			} catch (e: any) {
				// Revert optimistic update on error
				setSectorsWithPlants((prev) =>
					prev.Map((sector) => ({
						...sector,
						plants: sector.plants.Map((plant) => {
							if (plant.userRoleId !== userRoleId) return plant;
							return {
								...plant,
								notes: plant.notes.Map((note) =>
									note.id === noteId
										? { ...note, disabled: value }
										: note
								),
							};
						}),
					}))
				);
				snack.open(
					result.Err(
						e?.response?.data?.message ||
							e?.message ||
							"Failed to update notification setting."
					)
				);
			}
		},
		[handlePermissionError, snack]
	);

	return {
		sectorsWithPlants,
		loading,
		refetch: fetchUserRoleNotes,
		updateNotificationSetting,
	};
}
